import {applyOperation} from '@github-ui/optimistic-connection'
import {useConnectionMutation} from '@github-ui/optimistic-connection/useConnectionMutation'
import type {RouteQueryKey} from '@github-ui/react-core/query-key'
import {useQueryClient} from '@github-ui/react-query'
import type {UpdateLabelMutationMutation} from '@github-ui/server-persisted-queries/types/updateLabelMutation'
import {useCallback, useRef} from 'react'

import {LABELS} from './constants/labels'
import type {LabelsQueryData} from './types'
import {
  buildConfirmedEditLabelOperation,
  buildEditLabelOperation,
  type LabelInput,
  labelInputFromOperation,
  LABELS_CONNECTION_PATH,
} from './utils/label-connection-operations'
import {updateLabelRequest} from './utils/label-requests'

type UseEditLabelOptions = {
  labelId: string
  repositoryId: string
  repositoryNameWithOwner: string
  queryKey?: RouteQueryKey
  setEditSubmissionErrors: (v: string) => void
  setIsEditDialogOpen: (v: boolean) => void
}

type EditLabelHandler = (input: LabelInput, setIsSubmitting: (isSubmitting: boolean) => void) => void

export function useEditLabel({
  labelId,
  repositoryId,
  repositoryNameWithOwner,
  queryKey,
  setEditSubmissionErrors,
  setIsEditDialogOpen,
}: UseEditLabelOptions): EditLabelHandler {
  const queryClient = useQueryClient()
  const setIsSubmittingRef = useRef<(isSubmitting: boolean) => void>(() => {})

  const {mutate} = useConnectionMutation<UpdateLabelMutationMutation, Error>({
    ownerId: repositoryId,
    connectionPath: LABELS_CONNECTION_PATH,
    // The operation's fields carry the raw input the write needs.
    mutationFn: async operation => {
      const input = labelInputFromOperation(operation)
      if (!input) throw new Error(LABELS.editLabelError)
      return updateLabelRequest(repositoryNameWithOwner, labelId, input)
    },
    onError: error => {
      setEditSubmissionErrors(error.message)
      setIsSubmittingRef.current(false)
      setIsEditDialogOpen(true)
    },
    onSuccess: data => {
      const updatedLabel = data.updateLabel?.label
      if (queryKey && updatedLabel) {
        queryClient.setQueryData<LabelsQueryData>(queryKey, base =>
          base ? applyOperation(base, repositoryId, buildConfirmedEditLabelOperation(labelId, updatedLabel)) : base,
        )
      }
    },
  })

  return useCallback(
    (input: LabelInput, setIsSubmitting: (isSubmitting: boolean) => void) => {
      setIsSubmittingRef.current = setIsSubmitting
      setEditSubmissionErrors('')
      setIsEditDialogOpen(false)
      mutate({operation: buildEditLabelOperation(labelId, input)})
    },
    [mutate, labelId, setEditSubmissionErrors, setIsEditDialogOpen],
  )
}
